import React, { useRef, useState } from 'react';
import { ChevronDown, Eye, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useUserContext } from '../../../../contexts/UserContext';
import { permission } from '../../../../config/permission';
import { uploadEpmTestAttachments, updateEpmTest } from '../../../../utils/APIs';
import { notifyAutoSaved } from '../useAutoSave';
import FilePreviewModal, { isImageFile, getFileName } from '../FilePreviewModal';
import { EPM_LIMITS } from '../../validation/epmRules';

const MockupsSection = ({ testData, setTestData }) => {
    const { id } = useParams();
    const { userData, checkPermission } = useUserContext();
    const canUpdate = checkPermission(permission.XCM_TESTS_UPDATE);
    const fileInputRef = useRef(null);
    const [open, setOpen] = useState(true);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState(null);
    const [imgErrors, setImgErrors] = useState({});
    const [previewIndex, setPreviewIndex] = useState(null);

    const token = userData?.token;
    const orgToken = userData?.selectedOrganization?.org_token;
    const testId = testData?._id || testData?.id || id;

    // Stored mockups: array of { name, url, key }
    const mockups = testData?.ideation?.mockups || [];

    const saveMockups = async (updatedMockups) => {
        if (!canUpdate) return;
        // Optimistic update
        setTestData((prev) => ({
            ...prev,
            ideation: { ...prev?.ideation, mockups: updatedMockups },
        }));
        if (testId && token && orgToken) {
            try {
                await updateEpmTest(token, orgToken, testId, {
                    ideation: { ...testData?.ideation, mockups: updatedMockups },
                });
                notifyAutoSaved();
            } catch (err) {
                console.error('Failed to save mockups:', err);
            }
        }
    };

    const handleFileChange = async (e) => {
        if (!canUpdate) return;
        const files = Array.from(e.target.files);
        if (!files.length) return;

        setUploadError(null);

        if (mockups.length + files.length > EPM_LIMITS.MOCKUPS_MAX_COUNT) {
            setUploadError(`Maximum ${EPM_LIMITS.MOCKUPS_MAX_COUNT} files allowed per test. You currently have ${mockups.length} file(s).`);
            if (fileInputRef.current) fileInputRef.current.value = '';
            return;
        }

        setIsUploading(true);

        try {
            const formData = new FormData();
            formData.append('test_id', testId);
            formData.append('folder', 'mockups');
            files.forEach((file) => formData.append('files', file));

            const res = await uploadEpmTestAttachments(token, orgToken, formData);
            const uploaded = res?.data || [];

            const newMockups = [
                ...mockups,
                ...uploaded.map((f) => ({ name: f.originalname, url: f.url, key: f.key })),
            ];
            await saveMockups(newMockups);
        } catch (err) {
            console.error('Upload failed:', err);
            setUploadError('Upload failed. Please try again.');
        } finally {
            setIsUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleRemove = async (index) => {
        if (previewIndex === index) {
            setPreviewIndex(null);
        } else if (previewIndex !== null && previewIndex > index) {
            setPreviewIndex(previewIndex - 1);
        }
        const updated = mockups.filter((_, i) => i !== index);
        await saveMockups(updated);
    };

    const count = mockups.length;

    return (
        <section className="epm-idea__card" id="epm-field-mockups">
            <button
                className="epm-idea__disclosure"
                type="button"
                onClick={() => setOpen((o) => !o)}
                aria-expanded={open}
            >
                <ChevronDown className={open ? '' : '-rotate-90'} size={17} />
                <strong>Mockups &amp; attachments</strong>
                <span>{count > 0 ? `${count} of ${EPM_LIMITS.MOCKUPS_MAX_COUNT} files` : `No files yet (max ${EPM_LIMITS.MOCKUPS_MAX_COUNT})`}</span>
                <em>{open ? 'Hide' : 'Show'}</em>
            </button>

            {open && (
                <div className="epm-idea__attachments">
                    {mockups.map((file, i) => {
                        const fileName = getFileName(file);
                        const isImg = isImageFile(file) && !imgErrors[file.key || file.url || i];

                        return (
                            <div
                                key={file.key || file.url || i}
                                className={`epm-idea__attachment-card group relative select-none ${
                                    isImg ? '!p-0 !border-solid !border-[#dce1ee] cursor-pointer' : ''
                                }`}
                                onClick={() => {
                                    if (isImg) {
                                        setPreviewIndex(i);
                                    }
                                }}
                                title={isImg ? `${fileName} (Click to preview)` : fileName}
                            >
                                {isImg ? (
                                    <img
                                        src={file.url}
                                        alt={fileName}
                                        className="w-full h-full object-cover rounded-[7px] pointer-events-none"
                                        loading="lazy"
                                        onError={() => {
                                            setImgErrors((prev) => ({
                                                ...prev,
                                                [file.key || file.url || i]: true,
                                            }));
                                        }}
                                    />
                                ) : (
                                    <>
                                        <Paperclip size={15} style={{ color: '#8992a8', flexShrink: 0 }} />
                                        <a
                                            href={file.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            title={fileName}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            {fileName}
                                        </a>
                                    </>
                                )}

                                {/* Hover action icons with Tailwind */}
                                <div
                                    className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity rounded-[7px] bg-black/45"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {isImg ? (
                                        <button
                                            type="button"
                                            title="Preview image"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPreviewIndex(i);
                                            }}
                                            className="!bg-transparent !border-0 !p-0 !text-white hover:!text-white/80 cursor-pointer flex items-center shadow-none outline-none"
                                        >
                                            <Eye size={16} />
                                        </button>
                                    ) : <></>}
                                    {canUpdate && (
                                        <button
                                            type="button"
                                            title="Remove"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRemove(i);
                                            }}
                                            className="!bg-transparent !border-0 !p-0 !text-white hover:!text-red-400 cursor-pointer flex items-center shadow-none outline-none"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}

                    {canUpdate && (
                        <button
                            type="button"
                            disabled={isUploading || count >= EPM_LIMITS.MOCKUPS_MAX_COUNT}
                            onClick={() => fileInputRef.current?.click()}
                            className="disabled:opacity-60 disabled:cursor-not-allowed"
                            title={count >= EPM_LIMITS.MOCKUPS_MAX_COUNT ? 'Maximum 10 files per test reached' : undefined}
                        >
                            {isUploading ? (
                                <>
                                    <Loader2 size={13} className="animate-spin" />
                                    Uploading...
                                </>
                            ) : count >= EPM_LIMITS.MOCKUPS_MAX_COUNT ? (
                                <>Limit reached (10/10)</>
                            ) : (
                                <>
                                    <Upload size={13} />
                                    Add file
                                </>
                            )}
                        </button>
                    )}

                    {uploadError && (
                        <p className="w-full m-0 text-[11px] text-red-700">{uploadError}</p>
                    )}

                    <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        className="hidden"
                        onChange={handleFileChange}
                    />
                </div>
            )}

            {/* Reusable File Preview Modal */}
            {previewIndex !== null && (
                <FilePreviewModal
                    files={mockups}
                    currentIndex={previewIndex}
                    onClose={() => setPreviewIndex(null)}
                    onNavigate={setPreviewIndex}
                />
            )}
        </section>
    );
};

export default MockupsSection;
